import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, Clock, Store, ShoppingBag, Search } from "lucide-react";
import { BRANCHES, PLATFORMS } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const branchGelAlUrl = (branch) =>
  branch.gelal_url || `/gel-al?sube=${branch.code}`;

const BranchCard = ({ branch }) => (
  <article
    data-testid={`branch-card-${branch.code.toLowerCase()}`}
    className="card-print flex flex-col p-6 md:p-8"
  >
    <div className="flex items-center justify-between gap-4">
      <span className="flex h-12 w-12 items-center justify-center border-2 border-[#1A1A1A] bg-[#E63946] text-white">
        <Store size={22} strokeWidth={2.5} />
      </span>
      <span className="border-2 border-[#1A1A1A] bg-[#FDFBF7] px-3 py-1 text-xs font-black uppercase tracking-widest text-[#1A1A1A]">
        Şube Kodu: {branch.code}
      </span>
    </div>
    <h3 className="mt-5 text-2xl font-black uppercase tracking-tight text-[#1A1A1A]">
      {branch.name}
    </h3>
    <div className="mt-4 space-y-2 text-sm font-medium text-[#4A4A4A]">
      <p className="flex items-start gap-2">
        <MapPin size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-[#E63946]" />
        <span data-testid={`branch-address-${branch.code.toLowerCase()}`}>{branch.address}</span>
      </p>
      <p className="flex items-start gap-2">
        <Clock size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-[#E63946]" />
        <span data-testid={`branch-hours-${branch.code.toLowerCase()}`}>{branch.hours}</span>
      </p>
    </div>
    <div className="mt-6 flex flex-wrap items-center gap-4">
      <a
        data-testid={`branch-gelal-link-${branch.code.toLowerCase()}`}
        href={branchGelAlUrl(branch)}
        target={branch.gelal_url ? "_blank" : undefined}
        rel={branch.gelal_url ? "noopener noreferrer" : undefined}
        className="btn-chunky bg-[#E63946] px-4 py-2 text-xs uppercase tracking-wide text-white"
      >
        <ShoppingBag size={14} strokeWidth={2.5} />
        Gel Al Sipariş
      </a>
    </div>
    {PLATFORMS.some((p) => branch[p.key]) && (
      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-dashed border-[#1A1A1A] pt-4" data-testid={`branch-platforms-${branch.code.toLowerCase()}`}>
        {PLATFORMS.filter((p) => branch[p.key]).map((p) => (
          <a
            key={p.key}
            data-testid={`branch-platform-${branch.code.toLowerCase()}-${p.key}`}
            href={branch[p.key]}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center border-2 border-[#1A1A1A] bg-white px-3 py-2 transition-transform hover:-translate-y-0.5"
            aria-label={`${branch.name} — ${p.name} sayfası`}
          >
            <img src={p.logo} alt={`${p.name} logosu`} className="h-5 w-auto" loading="lazy" />
          </a>
        ))}
      </div>
    )}
  </article>
);

export const BranchesSection = () => {
  const [branches, setBranches] = useState(BRANCHES);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const load = () =>
      axios
        .get(`${API}/branches`)
        .then((res) => {
          if (Array.isArray(res.data) && res.data.length > 0) setBranches(res.data);
        })
        .catch(() => {});
    load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, []);

  const filtered = branches.filter(
    (b) =>
      !query.trim() ||
      `${b.code} ${b.name} ${b.address}`.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <section id="subeler" data-testid="branches-section" className="py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <p className="font-script text-2xl text-[#E63946] md:text-3xl">Şubelerimiz</p>
        <h2 className="mt-2 text-4xl font-black uppercase tracking-tighter text-[#1A1A1A] sm:text-5xl">
          Bize yakınsan,
          <br />
          lezzete yakınsın.
        </h2>
        <div className="relative mt-8 max-w-md">
          <Search size={18} strokeWidth={2.5} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
          <input
            data-testid="branch-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full border-2 border-[#1A1A1A] bg-white py-3 pl-11 pr-4 text-sm font-medium text-[#1A1A1A] outline-none focus:shadow-[3px_3px_0_0_#E63946]"
            placeholder="Şube ara (örn: Atakum, Kayseri...)"
          />
        </div>
        <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((b) => (
            <BranchCard key={b.code} branch={b} />
          ))}
        </div>
        {filtered.length === 0 && (
          <p data-testid="branch-search-empty" className="mt-10 text-sm font-medium text-[#4A4A4A]">
            Aramanızla eşleşen şube bulunamadı.
          </p>
        )}
      </div>
    </section>
  );
};
